import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Play, CheckCircle, XCircle, Clock, AlertTriangle, FileSpreadsheet, Trash2, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';

export default function IngestionPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataSource, setDataSource] = useState('LOYALTY_PATRONS');
  const [isDragging, setIsDragging] = useState(false);
  const queryClient = useQueryClient();

  const { data: jobsData, isLoading: jobsLoading, refetch } = useQuery({
    queryKey: ['ingestion-jobs'],
    queryFn: () => api.getIngestionJobs({ limit: 20 }),
    refetchInterval: 5000,
  });

  // Map data source to appropriate mapping config
  const getMappingConfig = (source: string): string => {
    const mappings: Record<string, string> = {
      'LOYALTY_PATRONS': 'loyalty-patron',
      'EXCLUSION_LIST': 'exclusion-list',
      'REGULATORY_LIST': 'exclusion-list',
    };
    return mappings[source] || 'loyalty-patron';
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error('No file selected');
      return api.uploadFile(selectedFile, dataSource, getMappingConfig(dataSource));
    },
    onSuccess: () => {
      setSelectedFile(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    },
  });

  const startMutation = useMutation({
    mutationFn: (jobId: string) => api.startIngestionJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    },
  });

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-detect data source based on filename
      const filename = file.name.toLowerCase();
      if (filename.includes('exclusion') || filename.includes('excluded')) {
        setDataSource('EXCLUSION_LIST');
      } else if (filename.includes('regulatory') || filename.includes('sanction')) {
        setDataSource('REGULATORY_LIST');
      }
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    const fileName = file?.name.toLowerCase() || '';
    const isValidFile = fileName.endsWith('.csv') ||
                        fileName.endsWith('.xlsx') ||
                        fileName.endsWith('.xls') ||
                        fileName.endsWith('.xlsm');
    if (file && isValidFile) {
      setSelectedFile(file);
      // Auto-detect data source based on filename
      if (fileName.includes('exclusion') || fileName.includes('excluded')) {
        setDataSource('EXCLUSION_LIST');
      } else if (fileName.includes('regulatory') || fileName.includes('sanction')) {
        setDataSource('REGULATORY_LIST');
      }
    }
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const dataSourceOptions = [
    { value: 'LOYALTY_PATRONS', label: 'Loyalty Patrons', description: 'Customer loyalty program data' },
    { value: 'EXCLUSION_LIST', label: 'Exclusion List', description: 'Denied Access Program (DAP) data' },
    { value: 'REGULATORY_LIST', label: 'Regulatory List', description: 'Regulatory compliance data' },
  ];

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Ingestion</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Upload and process data files for entity resolution</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </motion.div>

      {/* Upload Section */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <Upload className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Data File</h2>
            </div>
          </CardHeader>
          <CardContent>
            {/* Data Source Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Data Source Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {dataSourceOptions.map((option) => (
                  <motion.button
                    key={option.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDataSource(option.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      dataSource === option.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <p className={`font-medium ${
                      dataSource === option.value
                        ? 'text-primary-700 dark:text-primary-400'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {option.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{option.description}</p>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Drop Zone */}
            <motion.div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              animate={{
                scale: isDragging ? 1.02 : 1,
                borderColor: isDragging ? '#3b82f6' : selectedFile ? '#10b981' : '#e5e7eb',
              }}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                selectedFile
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                  : isDragging
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <AnimatePresence mode="wait">
                {selectedFile ? (
                  <motion.div
                    key="file-selected"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="flex flex-col items-center"
                  >
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                      <FileSpreadsheet className="h-10 w-10 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-white text-lg">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Trash2 className="h-4 w-4" />}
                      onClick={() => setSelectedFile(null)}
                      className="mt-4 text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      Remove
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="drop-zone"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                  >
                    <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full inline-block mb-4">
                      <Upload className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">
                      Drag and drop a CSV or Excel file here
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">or</p>
                    <label>
                      <Button variant="secondary" className="cursor-pointer">
                        Browse Files
                      </Button>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls,.xlsm"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                      Supported formats: CSV, XLSX, XLS, XLSM
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Upload Actions */}
            <AnimatePresence>
              {selectedFile && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={dataSource === 'EXCLUSION_LIST' || dataSource === 'REGULATORY_LIST' ? 'danger' : 'info'}>
                      {dataSourceOptions.find(o => o.value === dataSource)?.label}
                    </Badge>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Ready to upload
                    </span>
                  </div>
                  <Button
                    variant="primary"
                    leftIcon={<Upload className="h-4 w-4" />}
                    onClick={() => uploadMutation.mutate()}
                    isLoading={uploadMutation.isPending}
                  >
                    Upload File
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error State */}
            <AnimatePresence>
              {uploadMutation.isError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3"
                >
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700 dark:text-red-400">Upload failed. Please try again.</span>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>

      {/* Jobs List */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ingestion Jobs</h2>
              </div>
              {jobsData?.jobs && jobsData.jobs.length > 0 && (
                <Badge variant="default">{jobsData.jobs.length} jobs</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {jobsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <Skeleton className="h-8 w-24 rounded-lg" />
                  </div>
                ))}
              </div>
            ) : jobsData?.jobs && jobsData.jobs.length > 0 ? (
              <div className="space-y-3">
                {jobsData.jobs.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          job.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : job.status === 'processing'
                              ? 'bg-blue-100 dark:bg-blue-900/30'
                              : job.status === 'failed'
                                ? 'bg-red-100 dark:bg-red-900/30'
                                : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          <FileSpreadsheet className={`h-5 w-5 ${
                            job.status === 'completed'
                              ? 'text-green-600 dark:text-green-400'
                              : job.status === 'processing'
                                ? 'text-blue-600 dark:text-blue-400'
                                : job.status === 'failed'
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-gray-600 dark:text-gray-400'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{job.originalFilename}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant={
                                job.dataSource === 'EXCLUSION_LIST' || job.dataSource === 'REGULATORY_LIST'
                                  ? 'danger'
                                  : 'info'
                              }
                              className="text-xs"
                            >
                              {job.dataSource}
                            </Badge>
                            <JobStatusBadge status={job.status} />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {job.totalRecords ? (
                          <div className="flex items-center gap-3">
                            <div className="w-32">
                              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <span>Progress</span>
                                <span>{Math.round((job.processedRecords / job.totalRecords) * 100)}%</span>
                              </div>
                              <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${(job.processedRecords / job.totalRecords) * 100}%` }}
                                  transition={{ duration: 0.5 }}
                                  className={`h-full rounded-full ${
                                    job.status === 'completed'
                                      ? 'bg-green-500'
                                      : job.status === 'failed'
                                        ? 'bg-red-500'
                                        : 'bg-primary-500'
                                  }`}
                                />
                              </div>
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400 hidden md:block">
                              {job.processedRecords.toLocaleString()} / {job.totalRecords.toLocaleString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )}

                        {job.status === 'uploaded' && (
                          <Button
                            variant="primary"
                            size="sm"
                            leftIcon={<Play className="h-4 w-4" />}
                            onClick={() => startMutation.mutate(job.id)}
                            isLoading={startMutation.isPending}
                          >
                            Start
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full inline-block mb-4">
                  <FileText className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">No ingestion jobs yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Upload a file to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: typeof CheckCircle; variant: 'success' | 'info' | 'default' | 'danger' | 'warning' }> = {
    completed: { icon: CheckCircle, variant: 'success' },
    processing: { icon: Clock, variant: 'info' },
    uploaded: { icon: FileText, variant: 'default' },
    failed: { icon: XCircle, variant: 'danger' },
  };

  const { icon: Icon, variant } = config[status] || config.uploaded;

  return (
    <Badge variant={variant} className="flex items-center gap-1 text-xs capitalize">
      <Icon className="h-3 w-3" />
      {status}
    </Badge>
  );
}
